import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { netzanschlussService } from '../services/netzanschlussService';
import { useZoneStore } from './zoneStore';
import type { PlanningMeasure } from '../types/scenario';
import type { RemedyCandidate } from '../types/planning';
import type { ConnectionRequest, GreenfieldConnection, LoadCaseMode, NetzanschlussRemedyResult, RequestedAsset } from '../types/netzanschluss';

/**
 * Zone-scoped state of the Netzanschlussprüfung module. `clear()` is wired
 * into the zone-switch flow (ControlSidebar.handleZoneChange) — new
 * zone-scoped fields MUST be reset there too.
 *
 * Mutating actions (assign/recheck/decide) THROW on failure so the calling
 * component can toast the error; the list actions swallow and log (a stale
 * inbox is not an operator-facing failure).
 */
interface NetzanschlussState {
  selectedHausanschlussId: string | null;
  /** 'bestand' = existing HA (map click or search); 'neubau' = greenfield map pin (G3). */
  checkMode: 'bestand' | 'neubau';
  /** Greenfield input; lat/lng set by the map click. */
  newConnection: GreenfieldConnection | null;
  assets: RequestedAsset[];
  isChecking: boolean;
  /** Result shown in the workspace — a fresh check OR a persisted request loaded via showRequest(). */
  checkResult: ConnectionRequest | null;
  checkError: string | null;
  requests: ConnectionRequest[];
  isLoadingRequests: boolean;
  /** Portal requests without a resolved zone/HA — global, not zone-scoped. */
  unassignedRequests: ConnectionRequest[];
  /** Request ids with an in-flight assign/recheck/decide (per-row busy state). */
  busyRequestIds: string[];
  /** Per-check Lastfall override; null = deployment default (Settings). */
  loadCaseMode: LoadCaseMode | null;
  /** What-if grid measures the next check is evaluated with (verdict conditional on them). */
  measures: PlanningMeasure[];
  remedies: NetzanschlussRemedyResult | null;
  isFindingRemedies: boolean;
  remedyError: string | null;
}

interface NetzanschlussActions {
  setSelectedHausanschlussId: (id: string | null) => void;
  setCheckMode: (mode: 'bestand' | 'neubau') => void;
  setNewConnection: (nc: GreenfieldConnection | null) => void;
  setAssets: (assets: RequestedAsset[]) => void;
  setLoadCaseMode: (mode: LoadCaseMode | null) => void;
  setMeasure: (measure: PlanningMeasure) => void;
  removeMeasure: (measureId: string) => void;
  clearMeasures: () => void;
  /** "Abhilfe finden" for the current selection/assets (nothing persisted). */
  findRemedies: () => Promise<void>;
  /** Takes a candidate's measures over and re-checks with them. */
  applyRemedy: (candidate: RemedyCandidate) => Promise<void>;
  /** Drops the shown result (map goes back to live/archive) but keeps point, profile and assets. */
  clearResult: () => void;
  runCheck: () => Promise<void>;
  /** Loads a persisted request (inbox) into the workspace: selection, assets and result. */
  showRequest: (request: ConnectionRequest) => void;
  loadRequests: () => Promise<void>;
  loadUnassignedRequests: () => Promise<void>;
  /** hausanschlussId empty for greenfield requests (zone-only assignment). Throws on failure. */
  assignRequest: (id: string, zoneId: string, hausanschlussId?: string) => Promise<void>;
  /** Throws on failure. */
  recheckRequest: (id: string) => Promise<void>;
  /** Throws on failure. */
  decideRequest: (id: string, decision: 'APPROVED' | 'REJECTED') => Promise<void>;
  clear: () => void;
}

/** A Neubau is a dwelling until the operator says otherwise (DSO form default). */
const DEFAULT_PROFILE = { kind: 'residential', dwellingUnits: 1, electricWaterHeating: false } as const;

/** Greenfield pin placed? (the profile can exist before the map click). */
export const hasPin = (nc: GreenfieldConnection | null): nc is GreenfieldConnection =>
  nc != null && Number.isFinite(nc.lat) && Number.isFinite(nc.lng);

const initialState: NetzanschlussState = {
  selectedHausanschlussId: null,
  checkMode: 'bestand',
  newConnection: null,
  assets: [{ type: 'pv', pKw: 10, phases: 3 }],
  isChecking: false,
  checkResult: null,
  checkError: null,
  requests: [],
  isLoadingRequests: false,
  unassignedRequests: [],
  busyRequestIds: [],
  loadCaseMode: null,
  measures: [],
  remedies: null,
  isFindingRemedies: false,
  remedyError: null,
};

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

export const useNetzanschlussStore = create<NetzanschlussState & NetzanschlussActions>()(
  devtools((set, get) => ({
    ...initialState,

    setSelectedHausanschlussId: (id) =>
      set({ selectedHausanschlussId: id, checkResult: null, checkError: null }, false, 'setSelectedHausanschluss'),

    setCheckMode: (checkMode) =>
      set(s => {
        if (checkMode === s.checkMode) return s;
        // Neubau starts as a pure new connection (use profile, no asset) — assets
        // are optional add-ons there; Bestand always needs at least one asset.
        const assets = checkMode === 'neubau'
          ? []
          : s.assets.length > 0 ? s.assets : initialState.assets.map(a => ({ ...a }));
        const newConnection = checkMode === 'neubau'
          ? s.newConnection ?? { lat: NaN, lng: NaN, profile: { ...DEFAULT_PROFILE } }
          : s.newConnection;
        return { checkMode, assets, newConnection, checkResult: null, checkError: null };
      }, false, 'setCheckMode'),

    setNewConnection: (newConnection) =>
      set({ newConnection, checkResult: null, checkError: null }, false, 'setNewConnection'),

    setAssets: (assets) => set({ assets }, false, 'setAssets'),

    clearResult: () => set({ checkResult: null, checkError: null }, false, 'clearResult'),


    setLoadCaseMode: (loadCaseMode) => set({ loadCaseMode }, false, 'setLoadCaseMode'),

    setMeasure: (measure) => set(s => ({
      measures: [...s.measures.filter(m => m.id !== measure.id && !(m.kind === measure.kind && m.elementId === measure.elementId)), measure],
    }), false, 'setMeasure'),
    removeMeasure: (measureId) => set(s => ({ measures: s.measures.filter(m => m.id !== measureId) }), false, 'removeMeasure'),
    clearMeasures: () => set({ measures: [], remedies: null }, false, 'clearMeasures'),

    findRemedies: async () => {
      const { selectedHausanschlussId, assets, checkMode, newConnection, loadCaseMode, measures } = get();
      const zoneId = useZoneStore.getState().activeZoneId;
      const isNeubau = checkMode === 'neubau';
      if (!zoneId || (isNeubau ? !hasPin(newConnection) : !selectedHausanschlussId)) return;
      set({ isFindingRemedies: true, remedyError: null }, false, 'findRemedies/start');
      try {
        const override = loadCaseMode ? { loadCaseMode } : {};
        const body = isNeubau
          ? { newConnection: newConnection!, assets, persist: false, measures, ...override }
          : { hausanschlussId: selectedHausanschlussId!, assets, persist: false, measures, ...override };
        const remedies = await netzanschlussService.findRemedies(body);
        if (useZoneStore.getState().activeZoneId !== zoneId) return;
        set({ remedies, isFindingRemedies: false }, false, 'findRemedies/success');
      } catch (error) {
        set({ remedyError: errorMessage(error, 'Abhilfe-Suche fehlgeschlagen.'), isFindingRemedies: false }, false, 'findRemedies/error');
      }
    },

    applyRemedy: async (candidate) => {
      const { setMeasure } = get();
      for (const m of candidate.measures) {
        setMeasure({ ...m, id: m.id || crypto.randomUUID() });
      }
      set({ remedies: null }, false, 'applyRemedy');
      await get().runCheck();
    },

    runCheck: async () => {
      const { selectedHausanschlussId, assets, checkMode, newConnection, loadCaseMode, measures } = get();
      const zoneId = useZoneStore.getState().activeZoneId;
      const isNeubau = checkMode === 'neubau';
      if (!zoneId || (isNeubau ? !hasPin(newConnection) : !selectedHausanschlussId)) {
        set({
          checkError: isNeubau
            ? 'Bitte zuerst den Neubau-Standort auf der Karte anklicken.'
            : 'Bitte zuerst einen Hausanschluss auf der Karte oder in der Liste auswählen.',
        }, false, 'runCheck/noSelection');
        return;
      }

      set({ isChecking: true, checkError: null }, false, 'runCheck/start');
      try {
        const override = loadCaseMode ? { loadCaseMode } : {};
        const withMeasures = measures.length > 0 ? { measures } : {};
        const result = await netzanschlussService.runCheck(
          isNeubau
            ? { newConnection: newConnection!, assets, persist: true, ...override, ...withMeasures }
            : { hausanschlussId: selectedHausanschlussId!, assets, persist: true, ...override, ...withMeasures }
        );
        // Stale-zone guard: discard the response when the zone changed mid-flight.
        if (useZoneStore.getState().activeZoneId !== zoneId) return;
        set({ checkResult: result, isChecking: false, remedies: null }, false, 'runCheck/success');
      } catch (error) {
        if (useZoneStore.getState().activeZoneId !== zoneId) return;
        set({
          checkError: errorMessage(error, 'Unbekannter Fehler bei der Prüfung.'),
          isChecking: false,
        }, false, 'runCheck/error');
      }
    },

    showRequest: (request) => {
      const isNeubau = request.newConnection != null;
      set({
        checkMode: isNeubau ? 'neubau' : 'bestand',
        selectedHausanschlussId: isNeubau ? null : request.hausanschlussId || null,
        newConnection: isNeubau ? request.newConnection! : null,
        assets: request.requestedAssets,
        checkResult: request,
        checkError: null,
      }, false, 'showRequest');
    },

    loadRequests: async () => {
      const zoneId = useZoneStore.getState().activeZoneId;
      if (!zoneId) return;

      set({ isLoadingRequests: true }, false, 'loadRequests/start');
      try {
        const requests = await netzanschlussService.listRequests();
        if (useZoneStore.getState().activeZoneId !== zoneId) return;
        set({ requests, isLoadingRequests: false }, false, 'loadRequests/success');
      } catch (error) {
        console.warn('[netzanschlussStore] Anträge laden fehlgeschlagen:', error);
        set({ isLoadingRequests: false }, false, 'loadRequests/error');
      }
    },

    loadUnassignedRequests: async () => {
      // Global list (portal requests without zone) — no stale-zone guard needed.
      try {
        const unassignedRequests = await netzanschlussService.listUnassignedRequests();
        set({ unassignedRequests }, false, 'loadUnassignedRequests/success');
      } catch (error) {
        console.warn('[netzanschlussStore] Unzugeordnete Anträge laden fehlgeschlagen:', error);
      }
    },

    assignRequest: async (id, zoneId, hausanschlussId) => {
      set(s => ({ busyRequestIds: [...s.busyRequestIds, id] }), false, 'assignRequest/start');
      try {
        await netzanschlussService.assignRequest(id, zoneId, hausanschlussId);
        await Promise.all([get().loadUnassignedRequests(), get().loadRequests()]);
      } finally {
        set(s => ({ busyRequestIds: s.busyRequestIds.filter(x => x !== id) }), false, 'assignRequest/end');
      }
    },

    recheckRequest: async (id) => {
      const zoneId = useZoneStore.getState().activeZoneId;
      if (!zoneId) return;
      set(s => ({ busyRequestIds: [...s.busyRequestIds, id] }), false, 'recheckRequest/start');
      try {
        const updated = await netzanschlussService.recheckRequest(id);
        if (useZoneStore.getState().activeZoneId !== zoneId) return;
        await get().loadRequests();
        // Keep the workspace in sync when the rechecked request is the one on display.
        if (get().checkResult?.id === id) {
          set({ checkResult: updated }, false, 'recheckRequest/refreshShown');
        }
      } finally {
        set(s => ({ busyRequestIds: s.busyRequestIds.filter(x => x !== id) }), false, 'recheckRequest/end');
      }
    },

    decideRequest: async (id, decision) => {
      const zoneId = useZoneStore.getState().activeZoneId;
      if (!zoneId) return;
      set(s => ({ busyRequestIds: [...s.busyRequestIds, id] }), false, 'decideRequest/start');
      try {
        await netzanschlussService.decideRequest(id, decision);
        if (useZoneStore.getState().activeZoneId !== zoneId) return;
        await get().loadRequests();
      } finally {
        set(s => ({ busyRequestIds: s.busyRequestIds.filter(x => x !== id) }), false, 'decideRequest/end');
      }
    },

    clear: () => set({ ...initialState }, false, 'clear'),
  }))
);
